import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  differenceInCalendarDays as diffDays,
  isEqual,
} from 'date-fns';
import { Grid } from '@material-ui/core';
import { isEmpty } from 'lodash';
import Validation from '../../../Validation/Validation';
import DateRange from '../../../DateRange';
import { TRAVEL_MAX_DAYS } from '../../../../constants/travelCalcConstants';
import { DIGITS_PATTERN, EMPTY_NUMBER_FIELD_VALUE } from '../../../../constants/patterns';
import { isToday } from '../../../../helpers/dates';
import MainTextField from '../../../MUI/MainTextField';
import InfoText from '../../../MUI/IntoText';

function getContractDuration(endDate, startDate) {
  // startDate is start of the day, and endDate is the end of day, so adding + 1 day for duration
  return diffDays(endDate, startDate) + 1;
}

const DayPicker = ({
  setValue,
  errors,
  days,
  range,
  setDays,
  setRange,
  selectedPackage,
  updateDatesAccordingPackage,
  updateDatesOnDayChange,
  defaultMinDays,
  minStartDate,
  minEndDate,
}) => {
  const { t } = useTranslation();

  const updateDays = useCallback(
    (newDays, shouldValidate = true) => {
      setValue('days', newDays, shouldValidate);
      setDays(newDays);
    },
    [setDays, setValue],
  );

  const handleDayChange = (e) => {
    if (DIGITS_PATTERN.test(e.target.value)) {
      const newDays = Number(e.target.value || 0);
      updateDays(newDays);
      updateDatesOnDayChange(newDays);
    }
    if (isEmpty(e.target.value)) {
      updateDays(EMPTY_NUMBER_FIELD_VALUE);
      updateDatesOnDayChange(EMPTY_NUMBER_FIELD_VALUE);
    }
  };

  const startDateChangeHandler = useCallback((startDate) => {
    setValue('start', startDate, true);
    setRange({ start: startDate });
    if (selectedPackage.value) {
      updateDatesAccordingPackage(selectedPackage, startDate);
    } else if (
      // eslint-disable-next-line no-restricted-globals
      !isNaN(startDate) && !isNaN(range.end) && range.end
    ) { // when start and end here set days
      const newDays = getContractDuration(range.end, startDate);
      updateDays(newDays);
    } else if (!startDate && days) { // when removing start, clear days
      updateDays(0);
    }
  }, [
    days,
    range.end,
    selectedPackage,
    setRange,
    setValue,
    updateDatesAccordingPackage,
    updateDays,
  ]);

  const endDateChangeHandler = useCallback((endDate) => {
    if (!isEqual(range.end, endDate)) {
      setValue('end', endDate, true);
      setRange({ end: endDate });
      // when start and end here set days
      // eslint-disable-next-line no-restricted-globals
      if ((range.start && endDate) && (!isNaN(range.start) && !isNaN(endDate))) {
        const newDays = getContractDuration(endDate, range.start);
        updateDays(newDays);
      } else if (!endDate && days) { // when removing start, clear days
        updateDays(0);
      }
    }
  }, [days, range.end, range.start, setRange, setValue, updateDays]);

  return (
    <Grid
      container
      spacing={4}
    >
      <Grid item xs={12} sm={8}>
        <DateRange
          range={range}
          onStartDateChange={startDateChangeHandler}
          onEndDateChange={endDateChangeHandler}
          focusEndOnStartChange
          minRangeDays={defaultMinDays}
          maxRangeDays={TRAVEL_MAX_DAYS}
          startProps={{
            minDate: minStartDate,
          }}
          endProps={{
            minDate: minEndDate,
            // eslint-disable-next-line no-restricted-globals
            disabled: Boolean(selectedPackage.value) || (!range.start || isNaN(range.start)),
          }}
          labels={{
            start: 'travel.from',
            end: 'travel.to',
          }}
          validationProps={{
            errors,
            startName: 'start',
            endName: 'end',
          }}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Validation errors={errors} name="days">
          <MainTextField
            label="travel.days"
            value={days || ''}
            variant="outlined"
            type="number"
            size="small"
            fullWidth
            name="days"
            onChange={handleDayChange}
            disabled={Boolean(selectedPackage.value)}
            InputProps={{
              inputProps: {
                max: TRAVEL_MAX_DAYS,
                min: defaultMinDays,
              },
            }}
            required
          />
        </Validation>
      </Grid>
      {
        range && isToday(range.start) && (
          <Grid item xs={12}>
            <InfoText textInfo={t('travelContract.contractStartDate')} withHTML />
          </Grid>
        )
      }
    </Grid>
  );
};

DayPicker.propTypes = {
  days: PropTypes.number.isRequired,
  setDays: PropTypes.func.isRequired,
  minStartDate: PropTypes.instanceOf(Date).isRequired,
  minEndDate: PropTypes.instanceOf(Date).isRequired,
  range: PropTypes.shape({
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date),
  }),
  selectedPackage: PropTypes.shape({
    value: PropTypes.number,
    month: PropTypes.number,
    year: PropTypes.number,
  }),
  setRange: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  updateDatesAccordingPackage: PropTypes.func.isRequired,
  updateDatesOnDayChange: PropTypes.func.isRequired,
  errors: PropTypes.shape({}).isRequired,
  defaultMinDays: PropTypes.number.isRequired,
};

DayPicker.defaultProps = {
  range: {},
  selectedPackage: {},
};

export default DayPicker;
